// Auditoria: todas as ações da equipa (mais recentes primeiro), com filtro por ação e "antes → depois".
import { useApi } from "@ecliptica/api-client/react";
import { intlLocale, useTranslation, withoutInactiveLangs } from "@ecliptica/i18n";
import { Badge, Button, Card, EmptyState, NativeSelect, PageHeader, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ecliptica/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ScrollText } from "lucide-react";
import { useState } from "react";

/** Chave de tradução da ação ("users.role_change" → "audit.actions.users_role_change": o i18next usa "." para aninhar). */
const actionKey = (a: string) => `audit.actions.${a.replace(".", "_")}`;

const ACTIONS = [
  "auth.staff_sign_in",
  "users.role_change",
  "users.ban",
  "users.unban",
  "users.compensate",
  "users.note",
  "settings.update",
  "site.sections_change",
  "roles.create",
  "roles.update",
  "roles.reorder",
  "roles.delete",
];

/** "antes → depois" só das chaves que mudaram. Textos de línguas inativas (ex.: labels.pt) não se mostram. */
function Changes({ before, after }: { before: unknown; after: unknown }) {
  const b = withoutInactiveLangs(before ?? {}) as Record<string, unknown>;
  const a = withoutInactiveLangs(after ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k]));
  if (!keys.length) return null;
  const show = (v: unknown) => (v === undefined ? "—" : typeof v === "string" ? v : JSON.stringify(v));
  return (
    <ul className="flex flex-col gap-0.5 font-mono text-xs">
      {keys.map((k) => (
        <li key={k}>
          <span className="text-muted-foreground">{k}:</span> <span className="text-destructive line-through">{show(b[k])}</span> →{" "}
          <span className="text-success">{show(a[k])}</span>
        </li>
      ))}
    </ul>
  );
}

export function AuditPage() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const [action, setAction] = useState("");
  const q = useInfiniteQuery({
    queryKey: ["audit", action],
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) => {
      const res = await api.v1.admin.audit.$get({ query: { action: action || undefined, beforeId: pageParam?.toString(), limit: "50" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    getNextPageParam: (last) => last.nextBeforeId ?? undefined,
  });
  const fmt = new Intl.DateTimeFormat(intlLocale(i18n.language), { dateStyle: "short", timeStyle: "medium" });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHeader
        title={t("nav.audit")}
        description={t("audit.description")}
        actions={
          <NativeSelect aria-label={t("audit.filterAction")} value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">{t("audit.allActions")}</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {t(actionKey(a))}
              </option>
            ))}
          </NativeSelect>
        }
      />
      <Card>
        {q.isPending ? (
          <div className="flex flex-col gap-2 p-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<ScrollText />} title={t("audit.empty")} className="m-4" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("audit.when")}</TableHead>
                <TableHead>{t("audit.who")}</TableHead>
                <TableHead>{t("audit.what")}</TableHead>
                <TableHead>{t("audit.target")}</TableHead>
                <TableHead>{t("audit.details")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmt.format(new Date(r.at))}</TableCell>
                  <TableCell>
                    {r.actorName ?? t("audit.system")}
                    {r.actorEmail && <span className="block text-xs text-muted-foreground">{r.actorEmail}</span>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{i18n.exists(`app:${actionKey(r.action)}`) ? t(actionKey(r.action)) : r.action}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.targetType ? `${r.targetType}:${r.targetId ?? ""}` : "—"}</TableCell>
                  <TableCell>
                    <Changes before={r.before} after={r.after} />
                    {r.reason && (
                      <p className="mt-1 text-xs italic text-muted-foreground">
                        {t("audit.reason")}: {r.reason}
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      {q.hasNextPage && (
        <div className="mt-4 text-center">
          <Button variant="outline" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            {t("audit.more")}
          </Button>
        </div>
      )}
    </>
  );
}
